import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, Plug, Save, CheckCircle2, XCircle, Store, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ICashManager = () => {
    const { token } = useAuth();
    const auth = { Authorization: `Bearer ${token || localStorage.getItem('token')}` };
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [form, setForm] = useState({ user_id: '', warehouse_id: '', store_id: '', auto_sync: false, interval_min: 30 });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [lastReport, setLastReport] = useState(null);

    const load = useCallback(async () => {
        try {
            const [s, l] = await Promise.all([
                axios.get(`${API}/admin/icash/status`, { headers: auth }),
                axios.get(`${API}/admin/icash/logs`, { headers: auth }),
            ]);
            setStatus(s.data);
            setLogs(l.data.logs || []);
            const st = s.data.settings || {};
            setForm({
                user_id: st.user_id ?? '',
                warehouse_id: st.warehouse_id ?? '',
                store_id: st.store_id ?? '',
                auto_sync: !!st.auto_sync,
                interval_min: st.interval_min || 30,
            });
            if (s.data.last_sync) setLastReport(s.data.last_sync);
        } catch (e) { /* ignore */ }
        // eslint-disable-next-line
    }, []);

    useEffect(() => { load(); }, [load]);

    const saveSettings = async () => {
        setSaving(true);
        try {
            const payload = {
                auto_sync: form.auto_sync,
                interval_min: parseInt(form.interval_min) || 30,
            };
            if (form.user_id !== '') payload.user_id = parseInt(form.user_id);
            if (form.warehouse_id !== '') payload.warehouse_id = parseInt(form.warehouse_id);
            if (form.store_id !== '') payload.store_id = parseInt(form.store_id);
            await axios.post(`${API}/admin/icash/settings`, payload, { headers: auth });
            toast.success('Настройките са запазени');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при запазване');
        } finally { setSaving(false); }
    };

    const testConnection = async () => {
        setTesting(true); setTestResult(null);
        try {
            const res = await axios.post(`${API}/admin/icash/test`, {}, { headers: auth });
            setTestResult(res.data);
            res.data.success ? toast.success(res.data.message) : toast.error('Връзката не успя');
        } catch (e) {
            const msg = e.response?.data?.detail || 'Грешка при теста';
            setTestResult({ success: false, message: msg });
            toast.error(msg);
        } finally { setTesting(false); }
    };

    const pollUntilDone = async () => {
        for (let i = 0; i < 200; i++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
                const s = await axios.get(`${API}/admin/icash/status`, { headers: auth });
                if (!s.data.sync_running) {
                    setStatus(s.data);
                    if (s.data.last_sync) setLastReport(s.data.last_sync);
                    return s.data.last_sync;
                }
            } catch (e) { /* keep polling */ }
        }
        return null;
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            await axios.post(`${API}/admin/icash/sync`, {}, { headers: auth });
            toast.info('Синхронизацията стартира — това може да отнеме 1-3 минути…');
            const rep = await pollUntilDone();
            if (rep?.status === 'success') {
                toast.success(`Синхронизация завършена: ${rep.updated} обновени, ${rep.not_found} ненамерени`);
            } else if (rep) {
                toast.error(rep.error || 'Синхронизацията не успя');
            }
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Грешка при синхронизацията');
        } finally { setSyncing(false); }
    };

    const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('bg-BG') : '—';

    return (
        <div className="p-4 sm:p-6" data-testid="icash-manager">
            <h2 className="text-2xl font-bold flex items-center gap-2 mb-1"><Store className="w-6 h-6 text-emerald-600" /> iCash синхронизация</h2>
            <p className="text-sm text-muted-foreground mb-6">Автоматично изравняване на наличностите в онлайн магазина с физическия магазин (iCash POS) по баркод/SKU.</p>

            {status && !status.api_key_configured && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 text-sm flex items-start gap-2" data-testid="icash-no-keys-warning">
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                    <span>API ключът и секретът на iCash не са конфигурирани на сървъра (ICASH_API_KEY / ICASH_API_SECRET).</span>
                </div>
            )}

            <div className="grid gap-6 lg:grid-cols-2">
                {/* Settings card */}
                <div className="border rounded-xl p-4 sm:p-5 bg-white dark:bg-slate-800 dark:border-slate-700">
                    <h3 className="font-semibold mb-3 flex items-center gap-2"><Plug className="w-4 h-4" /> Настройки на връзката</h3>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">UserID (от iCash)</label>
                            <Input data-testid="icash-userid-input" type="number" value={form.user_id}
                                onChange={e => setForm(f => ({ ...f, user_id: e.target.value }))} placeholder="напр. 1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">WareHouseID (от iCash)</label>
                            <Input data-testid="icash-warehouseid-input" type="number" value={form.warehouse_id}
                                onChange={e => setForm(f => ({ ...f, warehouse_id: e.target.value }))} placeholder="напр. 1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">StoreID (обект/склад)</label>
                            <Input data-testid="icash-storeid-input" type="number" value={form.store_id}
                                onChange={e => setForm(f => ({ ...f, store_id: e.target.value }))} placeholder="напр. 1" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">Интервал (минути)</label>
                            <Input data-testid="icash-interval-input" type="number" min="5" value={form.interval_min}
                                onChange={e => setForm(f => ({ ...f, interval_min: e.target.value }))} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between mb-4 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-700/50">
                        <span className="text-sm font-medium">Автоматична синхронизация</span>
                        <Switch data-testid="icash-autosync-switch" checked={form.auto_sync}
                            onCheckedChange={v => setForm(f => ({ ...f, auto_sync: v }))} />
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button data-testid="icash-save-settings-btn" onClick={saveSettings} disabled={saving} size="sm">
                            <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Запазване…' : 'Запази настройки'}
                        </Button>
                        <Button data-testid="icash-test-btn" onClick={testConnection} disabled={testing} variant="outline" size="sm">
                            <Plug className="w-4 h-4 mr-1.5" /> {testing ? 'Тестване…' : 'Тест на връзката'}
                        </Button>
                    </div>
                    {testResult && (
                        <div data-testid="icash-test-result" className={`mt-3 p-3 rounded-lg text-sm flex items-start gap-2 ${testResult.success ? 'bg-emerald-50 dark:bg-emerald-950 border border-emerald-200' : 'bg-red-50 dark:bg-red-950 border border-red-200'}`}>
                            {testResult.success ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                            <div>
                                <div>{testResult.message}</div>
                                {testResult.stores?.length > 0 && (
                                    <div className="mt-1 text-xs text-muted-foreground">
                                        Обекти: {testResult.stores.map(s => `${s.StoreID || s.id || '?'} — ${s.StoreName || s.name || ''}`).join(', ')}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sync card */}
                <div className="border rounded-xl p-4 sm:p-5 bg-white dark:bg-slate-800 dark:border-slate-700">
                    <h3 className="font-semibold mb-3 flex items-center gap-2"><RefreshCw className="w-4 h-4" /> Синхронизация на наличности</h3>
                    <div className="text-sm text-muted-foreground mb-3">
                        Продукти с баркод/SKU в магазина: <b data-testid="icash-products-count">{status?.products_with_barcode_or_sku ?? '—'}</b><br />
                        Последна синхронизация: <b>{fmtDate(status?.settings?.last_sync_at)}</b>
                    </div>
                    <Button data-testid="icash-sync-now-btn" onClick={syncNow} disabled={syncing || status?.sync_running} className="mb-4">
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                        {syncing ? 'Синхронизиране…' : 'Синхронизирай сега'}
                    </Button>
                    {lastReport && (
                        <div data-testid="icash-last-report" className="text-sm border rounded-lg p-3 dark:border-slate-700">
                            <div className="font-medium mb-1 flex items-center gap-1.5">
                                {lastReport.status === 'success' ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                                Отчет — {fmtDate(lastReport.started_at)}
                            </div>
                            {lastReport.error ? (
                                <div className="text-red-600 text-xs">{lastReport.error}</div>
                            ) : (
                                <>
                                    <ul className="text-xs space-y-0.5 text-muted-foreground">
                                        <li>Артикули в iCash: <b>{lastReport.icash_items}</b></li>
                                        <li>Съвпаднали по баркод/SKU: <b>{lastReport.matched}</b></li>
                                        <li>Обновени наличности: <b className="text-emerald-600">{lastReport.updated}</b></li>
                                        <li>Без промяна: <b>{lastReport.unchanged}</b></li>
                                        <li>Ненамерени в iCash: <b className="text-amber-600">{lastReport.not_found}</b></li>
                                    </ul>
                                    {(lastReport.not_found_barcodes || []).length > 0 && (
                                        <div className="mt-2 p-2 rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800" data-testid="icash-not-found-list">
                                            <div className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Продукти с баркод, който липсва в iCash (проверете за грешка при изписване):</div>
                                            <ul className="text-xs space-y-0.5">
                                                {lastReport.not_found_barcodes.map((p, i) => (
                                                    <li key={i} className="text-amber-800 dark:text-amber-300">• {p.name} — <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">{p.barcode}</code></li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Logs */}
            <div className="mt-6 border rounded-xl p-4 sm:p-5 bg-white dark:bg-slate-800 dark:border-slate-700">
                <h3 className="font-semibold mb-3">История на синхронизациите</h3>
                {logs.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="icash-no-logs">Още няма извършени синхронизации.</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="icash-logs-table">
                            <thead>
                                <tr className="text-left text-xs text-muted-foreground border-b dark:border-slate-700">
                                    <th className="py-2 pr-3">Дата</th>
                                    <th className="py-2 pr-3">Тип</th>
                                    <th className="py-2 pr-3">Статус</th>
                                    <th className="py-2 pr-3">Обновени</th>
                                    <th className="py-2 pr-3">Ненамерени</th>
                                    <th className="py-2">Грешка</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map((l, i) => (
                                    <tr key={l.log_id || i} className="border-b last:border-0 dark:border-slate-700">
                                        <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDate(l.started_at)}</td>
                                        <td className="py-1.5 pr-3">{l.trigger === 'auto' ? 'Автоматична' : 'Ръчна'}</td>
                                        <td className="py-1.5 pr-3">{l.status === 'success' ? <span className="text-emerald-600">Успех</span> : <span className="text-red-600">Грешка</span>}</td>
                                        <td className="py-1.5 pr-3">{l.updated}</td>
                                        <td className="py-1.5 pr-3">{l.not_found}</td>
                                        <td className="py-1.5 text-xs text-red-600 max-w-[300px] truncate">{l.error || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ICashManager;
